export default {
  assembly: {
    contactProperties: 'Contact properties',
    convergenceAdvanced: 'Convergence (advanced)',
    allowCutbacks: 'Allow increment cutbacks',
    allowCutbacksHint:
      'Off by default: a step that will not converge fails immediately instead of retrying at smaller load increments. Turn on for a solve expected to be hard rather than wrong — it can take much longer to fail.',
    cutbacksDisabled: 'Disabled — fail fast',
    cutbacksEnabled: 'Enabled — retry smaller increments',
    friction: 'Friction coefficient μ (0–2)',
    frictionTooltip:
      'Coulomb friction carries shear up to μ times the current contact pressure. Blank or zero is frictionless, so the faces can slide freely.',
    normalStiffness: 'Normal stiffness (MPa/mm)',
    normalStiffnessTooltip:
      'Penalty stiffness resists normal penetration while the faces touch. A finite value permits small numerical overclosure; blank derives a value from the material stiffness.',
    stickSlope: 'Stick slope (MPa/mm)',
    stickSlopeTooltip:
      'Controls how quickly shear traction rises with relative tangential displacement. The pair sticks approximately until shear reaches μp, then slides. Blank derives K/100.',
    contactDefaults:
      'Blank friction is frictionless. Blank stiffness values are derived by the solver. Positive stiffness is required; stick slope requires an explicit friction coefficient.',
    shrinkTitle: 'Thermal-shrink preload',
    shrinkHint:
      'Apply an equivalent installed clamp to a fastener body using a fictitious temperature drop — this is not a heat-transfer solve and does not read a thermal_conduction field. Specify the force and axis. A section surface is optional and only calibrates the delivered force; this does not model rivet forming or create the section.',
    unnamed: 'Unnamed preload',
    name: 'Name',
    body: 'Fastener body',
    chooseBody: 'Choose a body',
    force: 'Target preload (N)',
    boltGrade: 'Bolt property class',
    boltGradeNone: 'Not specified',
    boltGradeHint: 'Sets proof stress for the preload margin.',
    areaMode: 'Shank model',
    threaded: 'Metric thread',
    smooth: 'Smooth shank / rivet',
    area: 'Shank area (mm²)',
    size: 'Metric size (M3–M36)',
    axis: 'Axis {{axis}}',
    section: 'Section surface (optional)',
    sectionNone: 'None — uncalibrated',
    sectionEmpty:
      'This mesh offers no section surface. The deck excludes interior surfaces, and a fastener shank has no mid-length plane in the CAD to tag, so a fastened assembly usually has none to offer. Leave it unset.',
    sectionUncalibrated:
      'Without a section surface the shrink is not calibrated against a measured force. Expect the delivered preload to land roughly 15–25% under target.',
    remove: 'Remove {{name}}',
    addShrink: 'Add thermal-shrink preload',
    invalid:
      'Complete the preload name, body, positive force, nonzero axis and shank area or metric size. Contact stiffness must be positive and friction must be between 0 and 2.',
  },
  // OpenFOAM boundary-condition type labels — used by the BC picker
  // dropdown and the compact role subtext on each BC row. English source
  // strings live in `-data/types.ts::BC_TYPE_LABELS` (kept as the
  // canonical fallback + type/vocab reference); the keys below drive the
  // localized display via `bcTypeLabel(t, type)` / `bcTypeShortLabel`.
  bc: {
    velocityInlet: 'Velocity inlet',
    pressureOutlet: 'Pressure outlet',
    wallNoSlip: 'Wall (no-slip)',
    slip: 'Slip',
    symmetry: 'Symmetry',
    freestream: 'Freestream',
    // A detected port whose direction only the user can supply. Phrased as the
    // instruction it is, not as a state, so the select's placeholder tells the
    // user what to do rather than naming an absence.
    unset: 'Choose a direction',
    groups: {
      unset: 'Needs a direction',
      inlet: 'Inlets',
      outlet: 'Outlets',
      farfield: 'Farfield',
      symmetry: 'Symmetry',
      wall: 'Walls',
    },
    short: {
      inlet: 'Inlet',
      outlet: 'Outlet',
      wall: 'Wall',
      slip: 'Slip',
      symmetry: 'Symmetry',
      freestream: 'Freestream',
      unset: 'Choose',
    },
  },
  boundaryConditions: {
    untaggedTitle: 'Mesh is untagged',
    untaggedDescription:
      "The selected mesh has no named patches yet. Below are the geometry's auto-detected openings (read-only). Hover a row to highlight the matching face in the viewer; tag them in MeshGen to bring physics assignment online here.",
    tagInMeshgen: 'Tag in MeshGen',
    faceIndex: 'Face {{index}}',
    selectForBulk: 'Select {{name}} for bulk reassignment',
    editPatchName: 'Edit patch name',
    rename: 'Rename {{name}}',
    blBadgeTooltip:
      "Prism-wall patch from MeshGen's Boundary Layer panel. Defaults to wall physics; reassign the role if it should carry a different BC and it'll move to that group.",
    bodyWallTooltip:
      "Bulk body wall — every body-surface face that isn't a separately-tagged patch (e.g. the boundary-layer faces). One merged no-slip patch; selecting it highlights the whole body surface.",
    bodyWallAria: 'What is body_wall?',
    facesCount: '{{count}} faces',
    velocityLockedTitle:
      'Locked — this value comes from the study-wide inlet-velocity sweep above (one value applied per run).',
    velocityTitle:
      "Inlet velocity magnitude [m/s], applied uniformly along the study's flow axis ({{axis}}). For the standard external-flow domain box, this is freestream velocity. Not corrected for patch orientation — if your inlet isn't perpendicular to {{axis}}, the effective normal component is v·cos(angle).",
    pressureLockedTitle:
      'Locked — this value comes from the study-wide outlet-pressure sweep above (one value applied per run).',
    pressureTitle:
      'Outlet GAUGE pressure — relative to ambient, not absolute. Typing 100 Pa means 100 Pa above ambient, not 100 Pa absolute. Default 0 = outlet at ambient. Stored as kinematic (p/ρ in m²/s²); the FE divides by fluid density when you enter Pa/kPa/psi. Incompressible NS only solves for pressure differences, so this sets the datum.',
    gaugeLabel: 'Gauge',
    gaugeHint: ', relative to ambient. 0 = outlet at ambient.',
    patchNamingConvention: 'Patch naming convention',
    patchNamingBody:
      "The solver writes mesh patches with a {{prefix}} prefix and splits multi-face tags across {{suffix000}}/{{suffix001}} suffixes. Names shown above are your meshgen labels — if a run fails with \"patch names don't match the mesh\", check that you've tagged every external surface in MeshGen's Boundary Assignment.",
    emptyMesh:
      'Open Boundary Assignment in MeshGen to name your surface zones. Those names will appear here for physics assignment.',
    assignPhysics:
      'Assign physics to each mesh patch. Click a row to highlight its faces.',
    viewerHint:
      'Viewer is showing CAD tessellation for face preview — use the View dropdown in the viewer corner to switch to the meshed cell view.',
    selectedCount: '{{count}} selected',
    // Assigns the boundary-condition physics of the selected patches
    // (inlet / outlet / wall / freestream / symmetry). "Boundary properties"
    // reads clearer than "role" — and avoids the ambiguous 角色 ("character")
    // reading in zh. Translators: use the boundary/BC-property sense
    // (zh 邊界屬性, ja 境界プロパティ).
    assignRolePlaceholder: 'Assign boundary properties…',
    clear: 'Clear',
    selectAllGroup: 'Select all {{group}}',
    selectCategoryInGroup: 'Select all {{category}} in {{group}}',
    showGroupInViewer: 'Show {{group}} in viewer',
    hideGroupInViewer: 'Hide {{group}} in viewer',
    boundaryLayerFaces: 'Boundary layer faces',
    wall: 'wall',
    studyWideVelocitySweep_one:
      'Study-wide inlet velocity sweep — {{count}} value (one shared value, set in Inlet · Velocity above), not a per-patch sweep.',
    studyWideVelocitySweep_other:
      'Study-wide inlet velocity sweep — {{count}} values (one shared value, set in Inlet · Velocity above), not a per-patch sweep.',
    studyWidePressureSweep_one:
      'Study-wide outlet pressure sweep — {{count}} value (one shared value, set in Outlet · Pressure above), not a per-patch sweep.',
    studyWidePressureSweep_other:
      'Study-wide outlet pressure sweep — {{count}} values (one shared value, set in Outlet · Pressure above), not a per-patch sweep.',
  },
  thermal: {
    labelAdiabatic: 'Adiabatic',
    labelFixedTemperature: 'Fixed temperature',
    labelHeatFlux: 'Heat flux',
    labelConvection: 'Convection',
    labelRadiation: 'Radiation',
    conditionHelp: '{{condition}} physics',
    helpAdiabatic:
      'No heat crosses this face (zero normal heat flux). Other faces can still exchange heat.',
    helpFixedTemperature:
      'The solver holds this surface at the specified temperature. The heat needed to do so is an output, not a prescribed heat rate.',
    helpHeatFlux:
      'Prescribed heat rate per unit surface area (W/m²). Positive heat flux is heat flowing into the face; negative extracts heat.',
    bulkAssignPlaceholder: 'Set condition…',
    helpConvection:
      'Exchanges heat with the ambient fluid through the film coefficient h. Heat may enter or leave, depending on the surface and ambient temperatures.',
    helpRadiation:
      'Exchanges thermal radiation with the ambient. The surface both emits and absorbs; net flow depends on emissivity and the fourth powers of the two temperatures.',
    hint: 'Assign a thermal condition to each CalculiX surface from the mesh. Untagged faces stay adiabatic.',
    faceConditions: 'Face conditions ({{count}})',
    noFaces:
      'No CalculiX surfaces yet — tag faces in MeshGen or pick a mesh that exports calculix_surfaces.',
    temperature: 'Temperature',
    heatFlux: 'Heat flux',
    convectionH: 'Film coefficient h',
    ambientTemperature: 'Ambient temperature',
    emissivity: 'Emissivity',
    volumeSourceDisabled: 'Volumetric heat rate — coming soon.',
    pointHeatTitle: 'Point heat loads',
    pointHeatHint:
      'Apply total heat power to one exact mesh node. Node IDs change after remeshing; the solver checks the selected node.',
    pointNode: 'Mesh node ID',
    pointPick: 'Pick on mesh',
    pointPickCancel: 'Cancel picking',
    pointPickHint:
      'Hover to preview the nearest node on a surface facet, then click to use its exact mesh node ID. The viewer switches from CAD tessellation to the solved mesh while picking.',
    pointPower: 'Power (W)',
    pointAdd: 'Add point load',
    pointRemove: 'Remove point load at node',
    tieSection: 'TIED INTERFACES',
    createTie: 'Bond as tie',
    tieHint:
      'A tie equates temperature across two faces so heat can conduct between independently meshed bodies. First face is the dependent (slave) side — prefer the finer mesh.',
    tieSwap: 'Swap which side of {{name}} is dependent',
    tieSwapShort: 'Swap',
    tieRemove: 'Remove tie {{name}}',
    tieDirectionHint: 'dependent (slave) → independent (master)',
    tieTolerance: 'Position tolerance',
    tieToleranceFor: 'Position tolerance for {{name}}',
    independentBodiesNeedTie:
      '{{count}} independently meshed bodies have no thermal tie. Heat cannot cross between them until you bond touching faces as ties, or remesh with shared nodes.',
    categoryTemperature: 'Fixed Temperature',
    categoryTemperatureHottest: 'Hottest temperature',
    categoryTemperatureHotter: 'Hotter temperature',
    categoryTemperatureColder: 'Colder temperature',
    categoryTemperatureColdest: 'Coldest temperature',
    // Short rank suffixes on Setup face rows (ENG-2775); keep distinct from
    // the legend titles above so list copy stays compact in every locale.
    rankHottest: 'hottest',
    rankHotter: 'hotter',
    rankColder: 'colder',
    rankColdest: 'coldest',
    detailFixedTemperature: 'Fixed temperature',
    detailBonded: 'Bonded',
    // Setup side-panel detail prefixes (ENG-3002) — keep in sync with
    // `feaSetupTargets` English wires that `localizeSetupDetail` rewrites.
    detailHeatFlux: 'Heat flux',
    detailPointHeatLoad: 'Point heat load',
    detailConvection: 'Convection',
    detailRadiation: 'Radiation',
    detailNode: 'node',
    categoryHeatFlux: 'Heat Flux',
    categoryHeatPoint: 'Point heat load',
    categoryConvection: 'Convection',
    categoryRadiation: 'Radiation',
    categoryTiedInterface: 'Tied interface',
  },
  cht: {
    labelAdiabatic: 'Adiabatic',
    labelFixedTemperature: 'Fixed Temperature',
    labelConvection: 'Convection',
    labelSurfaceHeatFlux: 'Surface Heat Flux',
    chipFixedT: 'Fixed T {{value}} K',
    chipConvection: 'Convection (ambient)',
    convectionAmbientHint:
      'Purple = heat loss to ambient air (surroundings), not to the fluid.',
    chipHeatFlux: 'Heat flux {{value}} W/m²',
    chipAdiabatic: 'Adiabatic',
    facesCount: '{{count}} faces',
    chipVelocityInlet: 'Velocity inlet',
    fluidInletVelocity: 'Fluid Inlet Velocity',
    fluidInletVelocityInfo: 'Inlet velocity magnitude along the flow axis.',
    inletTemperature: 'Inlet Temperature',
    inletTemperatureInfo: 'Temperature of the fluid entering the domain.',
    chipReferenceOutlet: 'Reference outlet (0 Pa)',
    referenceOutletBody:
      'Fixed-reference outlet: gauge pressure pinned at 0 Pa. Steady-state CHT with a near-incompressible liquid is well-posed with velocity-in / reference-out — exposing a tunable outlet pressure would shift the same flow datum without changing the solution.',
    chipCoupled: 'Coupled',
    chipSolidFluidInterface: 'Solid↔fluid interface',
    expand: 'Expand',
    collapse: 'Collapse',
    interfaceHeatPathHint:
      'Green = solid↔fluid contact (heat into the coolant). Outer purple is ambient air, not this interface.',
    interfaceReadOnlyBody:
      'This is a mesh patch that already joins the touching solid and fluid faces. The solver owns that contact — there is no convection or heat-flux type to edit here. Set fluid BCs on Fluid Mesh Source and ambient convection on the purple exterior row.',
    interfaceExplainerLabel: 'About this solid–fluid interface',
    interfaceExplainer:
      'This row is the coupled mesh patch "{{patch}}". The mesher merged every face where this solid and the coolant touch into that one patch, so heat can cross. You cannot assign a boundary condition here — the solver already couples temperature across the contact. Hover or click the row to highlight those faces in the viewer.',
    interfaceSolidK: '{{region}} ({{material}}) · k {{k}} W/(m·K)',
    interfaceFluidK: '{{region}} ({{material}}) · k {{k}} W/(m·K)',
    interfaceSolidMaterial: '{{region}} · {{material}}',
    interfaceFluidMaterial: '{{region}} · {{material}}',
    interfaceFaceList: 'Body faces: {{faces}}',
    exteriorFaceList: 'Exterior faces: {{faces}}',
    interfaceVolumeLabel: 'Volume {{index}}',
    interfaceVolumeOther: 'Other faces',
    volumeFacesChip: '{{label}} · {{count}} faces',
    faceListToggle: 'Face list · {{count}}',
    chipNoSlipWall: 'No-slip wall',
    chipSlipWall: 'Slip wall',
    fluidWallNoSlipHint:
      'Coolant sticks to this wall (no-slip). CHT does not offer a slip swap on this patch.',
    fluidWallExplainerLabel: 'About this coolant wall',
    fluidWallExplainer:
      'This is the fluid-region wall patch. The solver always writes no-slip here so coolant has zero velocity at the wall. A slip wall is not available on CHT — farfield slip is for external flow, not this wetted interior.',
    fluidWallReadOnlyBody:
      'No-slip is required on the wetted coolant wall. Change materials or openings elsewhere; this patch type is not editable.',
    chipOpeningUnassigned: 'No direction set',
    openingRoleInfo:
      'Is coolant fed in through this port, or does it drain out? The mesher found the port but cannot tell which way the flow goes, so only you can say.',
    openingUnassigned: 'Not used — sealed shut',
    openingInlet: 'Coolant enters here',
    openingOutlet: 'Coolant leaves here',
    openingUnassignedBody:
      'This port is treated as a solid wall, as if it were plugged. That is a valid choice for a fill port you want closed — but at least one port has to let coolant in and one has to let it out, or there is nothing driving the flow.',
    openingInletShared:
      'Speed and temperature are shared by every port you mark as an inlet — there is one incoming stream, not one per port.',
    power: 'Power',
    powerInfo:
      'Total heat power into this face. FE divides by patch area to derive Flux below; Flux is what gets submitted.',
    flux: 'Flux',
    fluxInfo:
      'Surface heat flux. Positive = heat into the solid; negative = surface cooling.',
    noArea: 'no area',
    powerDisabled:
      'Patch area unavailable — Power input is disabled. Set Flux directly, or pick a mesh whose face_tags carry area data.',
    patchArea: 'Patch area: {{area}} cm²',
    solidFaceType: 'Solid face boundary type',
    solidFaceTypeInfo:
      'Adiabatic = perfectly insulated face. Fixed Temperature = surface held at a target T. Convection = heat exchange with ambient via a convection coefficient h.',
    temperature: 'Temperature',
    temperatureInfo: 'Surface temperature held fixed at this value.',
    convectionCoefficient: 'Convection Coefficient',
    convectionCoefficientInfo:
      'Heat-transfer coefficient h. Natural air ~5, forced air ~50, water ~1000+, boiling ~10k+.',
    ambientTemperature: 'Ambient Temperature',
    ambientTemperatureInfo: 'Free-stream temperature far from the surface.',
    adiabaticBody: 'No heat flux on this face — perfectly insulated.',
    emptyPatches:
      "Assign thermal & flow physics to each mesh patch. Tag your mesh's surface zones in MeshGen — they'll appear here as individual rows.",
    assignPhysics:
      'Assign thermal & flow physics to each mesh patch. Click a row to highlight its faces in the viewer.',
  },
  draftSetup: {
    stepPhysics: 'Physics & Boundaries',
    stepStudy: 'Study Design',
    geometries: 'Geometries',
    addGeometry: 'Add geometry',
    inputs: 'Inputs',
    meshInputsKeepSettings:
      'Existing mesh inputs keep their saved mesh settings. Switch the input kind to Geometries to configure on-the-fly meshing.',
    parameters: 'Parameters',
    boundaryConditions: 'Boundary Conditions',
    facesFromMesh: 'faces set in MeshGen',
    facesFromMeshTooltip:
      'Inlet, outlet, and wall faces were named in MeshGen and carried over with the mesh. Set the physics values here; to change the faces, re-mesh in MeshGen.',
    setBcBeforeRunning: 'Set at least one boundary condition before running.',
    solverSettings: 'Solver settings',
    run: 'Run',
    sectionMesh: 'Mesh',
    sectionSimulate: 'Simulate',
    starting: 'Starting...',
    runStudy: 'Run study',
    continueToSimulate: 'Continue to Simulate',
    exceededCap:
      '{{rows}} design points exceeds the {{cap}}-row cap — reduce sweeps or inputs',
    largeStudy: '{{count}} design points — large studies may take a while',
    failedToRun: 'Failed to run study',
    fileKind: 'File kind',
    geometriesToggle: 'Geometries',
    meshesToggle: 'Meshes',
    hideEnclosure: 'Hide enclosure',
    showEnclosure: 'Show enclosure',
    enclosure: 'Enclosure',
    chtSummary: 'CHT · {{flow}} · {{runs}}',
    cfdSummary: '{{parameters}} · {{flow}} · {{runs}}',
    meshInReason: {
      noGeometry: 'Add at least one geometry.',
      unmeshed_one: '{{count}} geometry still needs a completed mesh.',
      unmeshed_other: '{{count}} geometries still need a completed mesh.',
      processing_one:
        '{{count}} geometry is still being prepared (diagnosis or repair).',
      processing_other:
        '{{count}} geometries are still being prepared (diagnosis or repair).',
      needsAttention_one:
        '{{count}} geometry needs your attention before it can be meshed.',
      needsAttention_other:
        '{{count}} geometries need your attention before they can be meshed.',
      templateMismatch_one:
        '{{count}} picked mesh was made for another simulation type than this study runs — re-mesh it for this study, or untick it.',
      templateMismatch_other:
        '{{count}} picked meshes were made for another simulation type than this study runs — re-mesh them for this study, or untick them.',
      patchMismatch:
        'Patch names differ across meshes — name the same zones (inlet/outlet/wall) on every geometry.',
    },
    templateSummary: '{{parameters}} · {{template}} · {{runs}}',
    labelSummary: '{{label}} · {{runs}}',
    runsMobo: '{{count}} runs · MOBO',
    runsFitOnly: '{{count}} runs · fit only',
    runs: '{{count}} runs',
    paramValue: '{{count}} value',
    paramList: '{{count}} list',
    paramRange: '{{count}} range',
  },
  objectives: {
    title: 'Objectives',
    sobolSamples: 'Sobol samples',
    count: '{{count}} / {{max}}',
    emptyHint:
      'Add at least {{min}} objectives to run multi-objective optimization. Leave empty to sample the design space without optimizing.',
    belowMin:
      'MOBO needs ≥{{min}} objectives — the run will fall back to surrogate fit-only until a second objective is added.',
    addObjective: 'Add objective',
    addAnother: 'Add another',
    advanced: 'Advanced',
    maxIterations: 'Max iterations',
    candidatesPerIter: 'Candidates / iter',
    selectMetric: 'Select metric',
    unsupportedMetric: '{{key}} (unsupported)',
    notApplicableMetric: "{{label}} (doesn't apply here)",
    undeterminedMetric: "{{label}} (can't tell yet)",
    farFieldObjective_one:
      "This objective doesn't apply: the case is a body in a far-field box, so pressure drop and loss coefficients measure the box, not the part. It has to change before the study can run. Use a force or force coefficient instead.",
    farFieldObjective_other:
      "{{count}} objectives don't apply: the case is a body in a far-field box, so pressure drop and loss coefficients measure the box, not the part. It has to change before the study can run. Use a force or force coefficient instead.",
    undeterminedObjective_one:
      "We can't tell yet whether this case is a passage or a body in a far-field box, so the pressure-drop objective may not mean anything. Set the boundary conditions or pick a mesh first.",
    undeterminedObjective_other:
      "We can't tell yet whether this case is a passage or a body in a far-field box, so {{count}} pressure-drop objectives may not mean anything. Set the boundary conditions or pick a mesh first.",
    min: 'Min',
    max: 'Max',
    removeObjective: 'Remove objective',
  },
  runPlan: {
    tabulateCht:
      'Tabulate per-region temperatures and peak hot-spot temperature',
    tabulateCfd: 'Tabulate forces and per-case deltas',
    tabulateFeaStructural:
      'Tabulate stresses, displacement, and safety factors',
    tabulateFeaModal: 'Tabulate vibration modes and natural frequencies',
    generateDesignPoints_one: 'Generate {{count}} design point',
    generateDesignPoints_other: 'Generate {{count}} design points',
    meshAndSolveCase_one: 'Mesh and solve {{count}} {{case}} case',
    meshAndSolveCase_other: 'Mesh and solve {{count}} {{case}} cases',
    sobolSample:
      'Sobol-sample {{samples}} point{{samplesSuffix}} over {{ranges}} range parameter{{rangesSuffix}}',
    meshAndSolve_one: 'Mesh and solve {{count}} case',
    meshAndSolve_other: 'Mesh and solve {{count}} cases',
    fitSurrogate: 'Fit a Gaussian-process surrogate over {{responses}}',
    allResponses: 'all responses',
    suggestBatch: 'Suggest next batch via MOBO to {{goals}}',
    minimize: 'minimize',
    maximize: 'maximize',
    whenYouPressRun: 'When you press run',
  },
  autoRetry: {
    label: 'Automatic retry',
    hint: 'If a case fails, the AI reviews the failure, adjusts the solver settings, and runs it again.',
    maxAttempts: 'Retries per case',
    meshLabel: 'Automatic mesh retry',
    meshHint:
      'If meshing a geometry fails — or completes below the quality policy — the AI reviews the failure, adjusts the mesh settings, and runs it again.',
    meshMaxAttempts: 'Retries per mesh',
  },
  sampling: {
    samples: 'Samples',
    noRanges: 'Set at least one parameter to Range to use the Sobol sample.',
    advanced: 'Advanced',
    method: 'Method',
    sobolSequence: 'Sobol Sequence',
    seed: 'Seed',
    scramble: 'Scramble',
    scrambleTooltip:
      'Apply Owen scrambling to the Sobol sequence — improves uniformity without losing the low-discrepancy property.',
    totalDesignPoints: 'Total design points',
    sobolPointsCombos: '{{samples}} Sobol points × {{combos}} discrete combos',
    sobolPoints: '{{samples}} Sobol points',
  },
  structural: {
    categoryFixedSupport: 'Fixed Support',
    categoryRollerSupport: 'Roller Support',
    categoryLoadFace: 'Load Face',
    categoryLoadEdge: 'Load Edge',
    categoryLoadPatch: 'Point Load',
    // Setup side-panel detail wires (ENG-3002) — sentence case matches
    // `feaBoundaryTargets` / `feaAssemblyTargets` English detail strings.
    detailFixedSupport: 'Fixed support',
    detailFixedBody: 'Fixed body',
    detailRestraint: 'Restraint',
    detailRollerSupport: 'Roller support',
    detailFaceLoad: 'Face load',
    detailPressure: 'Pressure',
    detailAxialCoupling: 'Axial coupling',
    detailContact: 'Contact',
    detailFriction: 'Friction',
    detailFrictionless: 'frictionless',
    detailBothSides: 'both sides',
    detailSameContactSurfaces: 'same contact surfaces',
    detailCutPlanePreload: 'Cut-plane preload',
    detailThermalShrinkPreload: 'Thermal-shrink preload',
    detailSharedMeshJoin: 'Shared mesh join',
    detailPointOnly: 'point only',
    detailEdgeOnly: 'edge only',
    detailFaces_one: '{{count}} face',
    detailFaces_other: '{{count}} faces',
    loadPatch: 'Point Load',
    patchPlaceHint: 'Click a point on this face in the 3D view.',
    patchPlacing: 'Place point',
    patchPlace: 'Place point',
    patchReplace: 'Move point',
    patchSnapLabel: 'Point placement snap',
    patchSnapNode: 'Mesh node',
    patchSnapFace: 'Face centre',
    patchSnapFacet: 'Surface facet',
    loadCountOnFace: '{{count}} loads on {{label}}',
    patchAt: 'At {{x}}, {{y}}, {{z}} mm',
    patchRadius: 'Radius',
    patchRadiusHint:
      'A point load on a solid is a stress singularity — peak stress grows without limit as the mesh is refined. The radius is what makes the result meaningful: it spreads the same total force over a real contact area.',
    patchNoCenterWarning: 'Place a point — Point Load needs one to apply.',
    categoryFree: 'Free',
    collapse: 'Collapse',
    expand: 'Expand',
    bcTypeFor: 'BC type for {{label}}',
    free: 'Free',
    fixedSupport: 'Fixed Support',
    rollerSupport: 'Roller Support',
    loadFace: 'Load Face',
    loadEdge: 'Load Edge',
    pressure: 'Pressure',
    pressureValue: 'Pressure',
    nodalLoad: 'Nodal Load',
    bodyLoads: 'Body and coupled loads',
    gravity: 'Gravity',
    addGravity: 'Add gravity',
    magnitude: 'Magnitude',
    centrifugal: 'Centrifugal load',
    addCentrifugal: 'Add centrifugal load',
    axisDirection: 'Axis direction',
    axisPoint: 'Axis point (mm)',
    addAxialCoupling: 'Couple selected faces axially',
    selectFacesForCoupling:
      'Select one or more faces to create an axial coupling.',
    forceN: 'Force (N)',
    displacementMm: 'Displacement (mm)',
    addLoad: 'Add load:',
    addLoadFor: 'Add {{kind}} to {{label}}',
    bulkAddFaceLoad: 'Add face load to selected faces',
    contactSection: 'CONTACT',
    frictionlessLimit:
      'Contact is frictionless unless you set a friction coefficient on the pair. Without one, surfaces slide without resistance, which can make the joint too flexible and cannot represent a joint that relies on friction to prevent slip.',
    contactSolveLimit:
      'Contact makes the solve nonlinear: surfaces can open and close as the load changes. The solver may fail to find a stable solution.',
    contactSummary:
      'Compression-only contact; separation is free. Add friction to carry shear.',
    contactMechanicsTooltip:
      'While touching, penalty contact resists normal penetration but permits a small numerical overclosure. It carries no tension, so the faces can open when pressure falls to zero. Without friction they slide freely; with friction they stick approximately below the μp limit and slide beyond it. Use shared nodes for an interface that must remain bonded. Contact status makes the solve nonlinear.',
    linearElasticLimit:
      'Materials are linear elastic: permanent deformation is not modelled. Stress above yield means this assumption no longer holds; do not treat it as a predicted physical stress.',
    fastenerSection: 'FASTENERS',
    fastenerPreload: 'Preload',
    createFastener: 'Add fastener',
    fastenerRemove: 'Remove fastener {{name}}',
    fastenerCutWarning:
      'A fastener needs an internal surface CUTTING the shank, on a mesh where every element next to the cut has a full face on it (a structured or swept layer). CalculiX leaves edge-touching elements bonded across the cut, so a cut through a general tetrahedral mesh is refused rather than silently under-delivering the preload.',
    contactHint:
      'Contact carries compression only and lets the surfaces separate — unlike a bonded mesh, which also carries tension. The first face is the dependent (slave) side; make it the finer or softer one.',
    createContact: 'Pair as contact',
    contactSwap: 'Swap which side of {{name}} is dependent',
    contactSwapShort: 'Swap',
    contactRemove: 'Remove contact {{name}}',
    contactDirectionHint: 'dependent (slave) → independent (master)',
    independentBodiesNeedContact:
      '{{count}} independently meshed bodies have no contact pair. Material assignment gives each body stiffness and density, but does not connect them; add contact pairs for touching interfaces or use a shared-node mesh for a bonded assembly.',
    clampedLoadWarning:
      'This face is fully fixed — a clamp prescribes every node\u2019s displacement, so these loads cannot act. Remove them, or free the face. (The shear a fixed support carries is a result of the solve, not an input.)',
    addLoadFaceTaken:
      'This face already has a face load — edit it above, or use its force components',
    removeLoad: 'Remove {{kind}}',
    sectionFacesLoads: '{{faces}} faces · {{loads}} loads',
    noEdgesForFace: 'No boundary edges available for this face.',
    noEdgeSelectedWarning: 'Select an edge — Load Edge needs one to apply.',
    selectedCount: '{{count}} selected',
    assignTypePlaceholder: 'Assign type…',
    clear: 'Clear',
    body: 'body',
    fixedBody: 'Fixed Support',
    bodyGroup: 'Body {{index}}',
    material: 'Material',
    bodiesAndMaterials: 'Bodies & materials',
    bodiesAndMaterialsTooltip:
      'Each included volume needs a structural material. Material supplies stiffness, density and strength, but does not connect separate bodies. Use shared nodes for a bonded interface or contact pairs where independently meshed bodies may touch.',
    targetBodies: 'Bodies',
    targetFaces: 'Faces',
    categoryTooltip: {
      fixed_face:
        'Prescribes zero displacement in X, Y and Z on every node of the face. Reactions are solve results.',
      roller_face:
        'Constrains only the selected displacement directions. Unconstrained directions remain free to move.',
      load_face:
        'Distributes the entered total force across the selected face nodes.',
      pressure:
        'Applies traction normal to the face. Positive pressure acts into the surface.',
      load_point:
        'Distributes a nodal force over the selected face; use Point Load when a physical loaded area is known.',
      load_edge:
        'Distributes the entered total force along one selected boundary edge.',
      load_patch:
        'Spreads the entered total force over a circular area around the chosen point, avoiding an ideal point-load stress singularity.',
      free: 'Adds no fixture or load. The face may still participate in contact.',
    },
    materialFor: 'Material for {{name}}',
    role: {
      member: 'Member',
      fastener: 'Fastener',
      excluded: 'Excluded',
    },
    noNamedFaces:
      'No named faces found. Tag faces in MeshGen before meshing to set boundary conditions.',
  },
  studyPreview: {
    title: 'Study Preview',
    designPointsLabel_one: 'design point',
    designPointsLabel_other: 'design points',
    designPointsFormula: '({{formula}})',
    formulaManualPart_one: '{{count}} manual',
    formulaManualPart_other: '{{count}} manual',
    emptyState: 'Select a file to preview the design point matrix',
    columnIndex: '#',
    columnIndexLong: 'DP #',
    columnIndexInfo:
      'Design Point — one row per case the study will run.\n\nSweep rows are generated from the cartesian expansion of the sweep values in Solver Settings. Manual rows are user-authored one-offs added with "+ Add design point".\n\nClick a manual DP row to edit its overrides. Hover any row to reveal duplicate + remove; hover a sweep-value cell to reveal its ✕.',
    columnGeometry: 'Geometry',
    columnElements: 'Elements',
    columnMeshId: 'Mesh ID',
    copyMeshId: 'Click to copy full Mesh ID',
    meshIdCopied: 'Mesh ID copied to clipboard',
    showingFirst: 'Showing first 200 of {{total}} design points',
    inputsMesh_one: '{{count}} mesh',
    inputsMesh_other: '{{count}} meshes',
    inputsGeometry_one: '{{count}} geometry',
    inputsGeometry_other: '{{count}} geometries',
    inputsMixed: '{{count}} inputs',
    addManualDp: 'Add design point',
    addManualDpTitle: 'Add a design point',
    editManualDpTitle: 'Edit design point',
    saveManualDp: 'Save',
    editingBanner: 'Editing DP: {{label}}',
    doneEditing: 'Done',
    editingSweepHint:
      'Sweep-field changes below apply to the sweep, not this DP. Edit the DP’s own overrides in its popover.',
    viewingStudyDefaults: 'Viewing study defaults',
    viewingSweepHint:
      'Field edits here become the study-wide baseline. Every design point that doesn’t override an axis inherits these values.',
    editingChipTooltip:
      'A manual DP is focused. The DP inherits the study defaults except for the axes you override in its popover. Sweep-field edits still affect the sweep list, not this DP. Click × to exit.',
    viewingChipTooltip:
      'Default authoring mode. Every field you edit here becomes the study-wide baseline — applied to every DP that doesn’t override that axis.',
    manualDpInput: 'Input',
    inputKindGeometry: 'Geo',
    inputKindMesh: 'Mesh',
    tagSweep: 'sweep',
    tagManual: 'manual',
    sweepRowFocusTitle: 'Viewing a sweep row',
    sweepRowFocusHint:
      'Edits below (fluid, BC, solver) apply to ALL sweep rows — sweep rows are every combination of the values you picked, not independent points. To pin a single point with its own settings, use "Add design point" to create a manual DP.',
    sweepRowFocusExit: 'Return to study defaults',
    tooltipInherit: 'inherits study default',
    tooltipUnique: 'unique',
    tooltipSharedWith: 'shared with {{rows}}',
    removeSweepValue: 'Remove this sweep value',
    removeSweepValueTooltip: 'Drop {{value}} from {{path}}',
    manualDpOverrides: 'Overrides',
    manualDpInherit: 'inherit',
    manualDpLabel: 'Tags (optional)',
    manualDpLabelPlaceholder: 'e.g. fast, open, worst-case',
    duplicateDp: 'Duplicate design point',
    duplicateAsManual: 'Duplicate as manual DP',
    manualDpColor: 'Color',
    removeManualDp: 'Remove this design point',
    manualDpChip_one: '{{count}} manual',
    manualDpChip_other: '{{count}} manual',
    multiSelectHintAria: 'Multi-select behavior on the DP table',
    multiSelectHint:
      'Ctrl / Cmd + click on a manual DP row to add it to the selection. Shift + click extends the range. Ctrl / Cmd + A selects every visible manual DP. Delete / Backspace removes the selection; Escape clears it.\n\nMulti-select works on manual DPs only for now. Sweep DPs share axis lists — removing one specific combo (e.g. row #4 = velocity=1 × pressure=20) has no data-model entry today. Tracked as a follow-up (BE: excluded_dp_signatures).',
    multiSelectSelected_one: '{{count}} selected',
    multiSelectSelected_other: '{{count}} selected',
    multiSelectDelete: 'Delete',
    multiSelectClear: 'Clear',
    selectAllManual: 'Select all',
    multiSelectStatusChip_one: '{{count}} DP selected',
    multiSelectStatusChip_other: '{{count}} DPs selected',
    multiSelectChipTooltip_one:
      '{{count}} manual DP is multi-selected. Sidebar changes apply to the whole study (or the sweep). To delete the selection use the Delete key or the Delete button next to the "manual" chip. Click × to clear the selection.',
    multiSelectChipTooltip_other:
      '{{count}} manual DPs are multi-selected. Sidebar changes apply to the whole study (or the sweep). To delete the selection use the Delete key or the Delete button next to the "manual" chip. Click × to clear the selection.',
    multiSelectSidebarHint_one:
      'Sidebar edits still apply to the study defaults / sweep — they do not modify the {{count}} selected DP. Use the Delete button or the Delete key to remove the selection, Escape to clear.',
    multiSelectSidebarHint_other:
      'Sidebar edits still apply to the study defaults / sweep — they do not modify the {{count}} selected DPs. Use the Delete button or the Delete key to remove the selection, Escape to clear.',
    cancel: 'Cancel',
  },
  bcValidation: {
    unsetOpening:
      'Choose a direction for {{patches}} — the mesher detected these as flow openings but cannot tell which way the flow goes. Left unset, they are sealed shut and the run reports a device with fewer ports than your CAD has.',
    missingInlet:
      'Pick at least one patch as a velocity inlet (or freestream) so the solver knows where flow enters.',
    missingOutlet:
      'Pick at least one patch as a pressure outlet (or freestream). Without one, the pressure equation is unbounded and the solver diverges.',
    patchMismatch:
      'Patch "{{patch}}" isn\'t in the current mesh — re-pick the role or remove the row.',
    zeroInletVelocity:
      'Set the study-wide inlet velocity (Inlet · Velocity in the physics settings) greater than 0 — a per-patch row value alone won’t clear this. The default 0 is a placeholder and the study cannot run with it.',
    zeroPatchVelocity:
      'Set a velocity greater than 0 on patch "{{patch}}" — 0 is a placeholder and the study cannot run with it.',
    jumpToField: 'Jump to the field',
  },
} as const;
